/* SessionsGeoMap — admin Overview tab widget that pins the last 20 admin
 * sign-ins on a world map, with the latest session pulsing in gold. Reads
 * /api/academy/admin/security/sessions-geo (cookie-auth, admin-only). */
import React, { useEffect, useState } from 'react';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { Globe2, Shield, KeyRound } from 'lucide-react';

const API = `${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;
// Public-domain TopoJSON of country borders, served from a stable jsdelivr CDN.
// Same dataset every react-simple-maps tutorial uses.
const TOPO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

function fmtTs(iso) {
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso || '—'; }
}

export default function SessionsGeoMap() {
  const [rows, setRows] = useState(null); // null = loading, [] = empty, [...] = data
  const [hovered, setHovered] = useState(null); // { row, x, y } | null

  useEffect(() => {
    let alive = true;
    fetch(`${API}/academy/admin/security/sessions-geo`, { credentials: 'include' })
      .then((r) => r.ok ? r.json() : { rows: [] })
      .then((d) => { if (alive) setRows(Array.isArray(d?.rows) ? d.rows : []); })
      .catch(() => alive && setRows([]));
    return () => { alive = false; };
  }, []);

  if (rows === null) {
    return (
      <div className="rounded-2xl border border-gray-200 bg-white p-6" data-testid="sessions-geo-map-loading">
        <div className="text-[12px] text-[#6E6E73]">Loading session map…</div>
      </div>
    );
  }
  // Filter to plottable rows (lat/lng != 0,0 means we have real geo).
  const plottable = rows.filter((r) => Math.abs(r.lat) + Math.abs(r.lng) > 0.01);
  const latest = plottable[0]; // already sorted desc by backend
  const countries = new Set(plottable.map((r) => r.country_code).filter((c) => c && c !== 'ZZ'));

  return (
    <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden" data-testid="sessions-geo-map">
      <header className="px-6 py-4 flex items-center justify-between border-b border-gray-100 bg-[#f8f8fa]">
        <div className="flex items-center gap-2">
          <Globe2 className="w-4 h-4 text-[#c9a227]" />
          <div>
            <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#6E6E73]">Admin sessions · last 20</div>
            <div className="text-[15px] font-extrabold text-[#0e0e10]">Sign-in map</div>
          </div>
        </div>
        <div className="flex items-center gap-3 text-[11px] text-[#6E6E73]">
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-[#c9a227] animate-pulse" /> Latest
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-[#0e0e10]/40" /> Older
          </span>
          <span className="hidden md:inline">·</span>
          <span data-testid="sessions-geo-stats" className="hidden md:inline">
            {plottable.length} sessions · {countries.size} {countries.size === 1 ? 'country' : 'countries'}
          </span>
        </div>
      </header>

      <div className="relative bg-[#0b0b0e]" style={{ height: 360 }}>
        <ComposableMap
          projection="geoEqualEarth"
          projectionConfig={{ scale: 165 }}
          width={980}
          height={360}
          style={{ width: '100%', height: '100%' }}
        >
          <Geographies geography={TOPO_URL}>
            {({ geographies }) => geographies.map((geo) => (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                style={{
                  default: { fill: '#1a1a1f', stroke: '#2a2a30', strokeWidth: 0.4, outline: 'none' },
                  hover:   { fill: '#222229', stroke: '#3a3a40', outline: 'none' },
                  pressed: { fill: '#222229', outline: 'none' },
                }}
              />
            ))}
          </Geographies>
          {plottable.map((row, i) => {
            const isLatest = i === 0;
            return (
              <Marker
                key={`${row.ip}-${row.ts}`}
                coordinates={[row.lng, row.lat]}
                onMouseEnter={(evt) => setHovered({ row, x: evt.clientX, y: evt.clientY })}
                onMouseLeave={() => setHovered(null)}
                data-testid={isLatest ? 'sessions-geo-latest' : `sessions-geo-pin-${i}`}
              >
                {isLatest && (
                  <>
                    <circle r={10} fill="#f5a623" opacity={0.18}>
                      <animate attributeName="r" from="6" to="14" dur="1.6s" repeatCount="indefinite" />
                      <animate attributeName="opacity" from="0.45" to="0" dur="1.6s" repeatCount="indefinite" />
                    </circle>
                    <circle r={5} fill="#f5a623" stroke="#fff" strokeWidth={1.5} />
                  </>
                )}
                {!isLatest && (
                  <circle r={3} fill="#fff" opacity={0.55} stroke="#0e0e10" strokeWidth={0.5} />
                )}
              </Marker>
            );
          })}
        </ComposableMap>

        {hovered && (
          <div
            className="pointer-events-none absolute z-10 bg-white text-[#0e0e10] rounded-lg shadow-xl border border-gray-200 px-3 py-2 text-[11px] leading-tight"
            style={{
              left: Math.min(hovered.x - 200, window.innerWidth - 240),
              top: hovered.y - 90,
              minWidth: 200,
            }}
          >
            <div className="font-bold text-[12px]">{hovered.row.city ? `${hovered.row.city}, ` : ''}{hovered.row.country}</div>
            <div className="text-[#6E6E73] font-mono mt-0.5">{hovered.row.ip}</div>
            <div className="text-[#6E6E73] mt-0.5">{fmtTs(hovered.row.ts)} · {hovered.row.ua_label}</div>
            <div className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-[#c9a227]">
              {hovered.row.via === 'totp' && <><Shield className="w-3 h-3" /> Authenticator</>}
              {hovered.row.via === 'recovery' && <><KeyRound className="w-3 h-3" /> Recovery code</>}
              {(!hovered.row.via || hovered.row.via === 'password') && <>Password</>}
            </div>
          </div>
        )}

        {plottable.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center text-white/60 text-[12px]">
            No geolocated sessions yet. Sign in once to populate the map.
          </div>
        )}
      </div>

      {latest && (
        <div className="px-6 py-3 border-t border-gray-100 flex items-center justify-between text-[12px]">
          <div className="text-[#6E6E73]">
            Latest: <span className="font-semibold text-[#0e0e10]">{latest.city ? `${latest.city}, ` : ''}{latest.country}</span>
            {' · '}
            <span className="font-mono">{latest.ip}</span>
            {' · '}
            {fmtTs(latest.ts)}
          </div>
          <div className="text-[#6E6E73]">
            via <span className="font-semibold text-[#0e0e10]">
              {latest.via === 'totp' ? 'Authenticator' : latest.via === 'recovery' ? 'Recovery code' : 'Password'}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
